import React, { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Catalog from './catalog';
import { stickyKitRecalc } from '../utils';
import { CatalogFilter, CourseCatalog, CourseType, RemoteActivity, RemoteOrganizer } from '../types';
import { fetchCatalogAction } from '../service/catalog';
import { fetchOrganizersAction } from '../service/organizers';
import { SelectedKrets } from './krets-actions';
import ErrorMessageBoundary from '../errors/error-message-boundary';

export interface CatalogProps extends CatalogFilter {
  title?: string;
}

interface ActivityFilterParams {
  activities: RemoteActivity[];
  kretsar: SelectedKrets[];
  searchFilterText: string;
}

function isTextMatch(item, searchFilterText = ''): boolean {
  return new RegExp(searchFilterText, 'i').test(item.title + ' ' + item.description);
}

function isKretsMatch(item, kretsar, organizer): boolean {
  return kretsar.find((k) => k.value === organizer);
}

function itemMatch(
  isKretsSearch: boolean,
  isFilterTextSearch: boolean,
  item: RemoteActivity,
  kretsar: SelectedKrets[],
  organizer?: number,
  searchFilterText?: string
): boolean {
  if (isKretsSearch && !isFilterTextSearch) {
    return isKretsMatch(item, kretsar, organizer);
  } else if (!isKretsSearch && isFilterTextSearch) {
    return isTextMatch(item, searchFilterText);
  } else if (isKretsSearch && isFilterTextSearch) {
    return isKretsMatch(item, kretsar, organizer) && isTextMatch(item, searchFilterText);
  }
  return false;
}

function filterActivities({ activities, kretsar, searchFilterText }: ActivityFilterParams): RemoteActivity[] {
  const filtered = [];
  const isFilterTextSearch = !!searchFilterText;
  const isKretsSearch = kretsar.length > 0;
  for (const item of activities) {
    const organizer = parseInt(item.organizer, 10);
    if (itemMatch(isKretsSearch, isFilterTextSearch, item, kretsar, organizer, searchFilterText)) {
      filtered.push(item);
    }
  }
  return filtered;
}

function filterTypes({
  types,
  kretsar,
  searchFilterText,
}: {
  types: CourseType[];
  kretsar: SelectedKrets[];
  searchFilterText?: string;
}): CourseType[] {
  const filtered = [];
  for (const item of types) {
    const activities = filterActivities({
      activities: item.activities,
      kretsar,
      searchFilterText,
    });
    if (activities && activities.length > 0) {
      filtered.push({
        ...item,
        activities: activities || [],
      });
    }
  }
  return filtered;
}

function filterCatalog({
  catalog,
  kretsar,
  searchFilterText,
}: {
  catalog: CourseCatalog[];
  kretsar: SelectedKrets[];
  searchFilterText?: string;
}): CourseCatalog[] {
  const filtered = [];
  for (const item of catalog) {
    const activities = filterActivities({
      activities: item.activities,
      kretsar,
      searchFilterText,
    });
    const types = filterTypes({ types: item.types, kretsar, searchFilterText });
    if ((types && types.length > 0) || (activities && activities.length > 0)) {
      filtered.push({
        ...item,
        activities: activities || [],
        types: types || [],
      });
    }
  }
  return filtered;
}

export default function CatalogContainer(props: CatalogProps): ReactElement {
  const reduxState = useSelector(
    (state: { catalog: CourseCatalog[]; organizers: RemoteOrganizer[]; selectedKretsar: SelectedKrets[]; searchFilterText: string }) => ({
      catalog: state.catalog,
      organizers: state.organizers,
      selectedKretsar: state.selectedKretsar,
      searchFilterText: state.searchFilterText,
    })
  );
  const filteredCatalog =
    reduxState.selectedKretsar.length > 0 || reduxState.searchFilterText
      ? filterCatalog({
          catalog: reduxState.catalog,
          kretsar: reduxState.selectedKretsar,
          searchFilterText: reduxState.searchFilterText,
        })
      : reduxState.catalog;
  const dispatch = useDispatch();
  const fetchOrganizers = useCallback(() => fetchOrganizersAction(dispatch), [dispatch]);
  useEffect(() => {
    stickyKitRecalc();
  });
  useEffect(() => {
    fetchCatalogAction(dispatch)(props);
  }, [dispatch, props]);
  useEffect(() => {
    fetchOrganizers();
  }, [fetchOrganizers]);
  return (
    <ErrorMessageBoundary name="catalog" errorIsClearable>
      <Catalog
        title={props.title}
        catalog={filteredCatalog}
        organizers={reduxState.organizers}
        selectedKretsar={reduxState.selectedKretsar}
        organizationId={props.organizationId}
        searchFilterText={reduxState.searchFilterText}
      />
    </ErrorMessageBoundary>
  );
}
CatalogContainer.displayName = 'CatalogContainer';
